/**
 * Checks and escapes values that are written into CSS text: a `<style>`
 * element or a style string rendered with dangerouslySetInnerHTML.
 *
 * React escapes props it renders as text or attributes, but CSS text is
 * passed through as-is, so a value such as
 * `x.woff2"); } body{display:none} .a{background:url("https://evil/`
 * closes the declaration it was meant for and adds rules of its own,
 * external `url()` loads included. Component props come from the Puck editor
 * (a person or the editor's agent) and from Beaker, so every value that goes
 * into CSS text goes through here first, whoever wrote it:
 *
 * - colours must be CSS colour syntax (hex, rgb/rgba/hsl/hsla, a colour
 *   name, `var(--name)`); anything else is dropped;
 * - URLs must be https or relative, and are emitted as an escaped
 *   `url("...")`; anything else is dropped;
 * - free text (a font family name) is emitted as an escaped CSS string;
 * - keywords (font weight and style) must be one of the CSS values.
 *
 * Values in React `style={{...}}` objects do not need this: the browser
 * parses each one as the value of its own property.
 */

/** Longer values are not colours, URLs or names anyone means to write. */
const MAX_LENGTH = 2048

/** CSS named colours (CSS Color 4), plus `transparent` and `currentcolor`. */
const NAMED_COLORS = new Set(
  (
    'aliceblue antiquewhite aqua aquamarine azure beige bisque black ' +
    'blanchedalmond blue blueviolet brown burlywood cadetblue chartreuse ' +
    'chocolate coral cornflowerblue cornsilk crimson cyan darkblue darkcyan ' +
    'darkgoldenrod darkgray darkgreen darkgrey darkkhaki darkmagenta ' +
    'darkolivegreen darkorange darkorchid darkred darksalmon darkseagreen ' +
    'darkslateblue darkslategray darkslategrey darkturquoise darkviolet ' +
    'deeppink deepskyblue dimgray dimgrey dodgerblue firebrick floralwhite ' +
    'forestgreen fuchsia gainsboro ghostwhite gold goldenrod gray green ' +
    'greenyellow grey honeydew hotpink indianred indigo ivory khaki lavender ' +
    'lavenderblush lawngreen lemonchiffon lightblue lightcoral lightcyan ' +
    'lightgoldenrodyellow lightgray lightgreen lightgrey lightpink ' +
    'lightsalmon lightseagreen lightskyblue lightslategray lightslategrey ' +
    'lightsteelblue lightyellow lime limegreen linen magenta maroon ' +
    'mediumaquamarine mediumblue mediumorchid mediumpurple mediumseagreen ' +
    'mediumslateblue mediumspringgreen mediumturquoise mediumvioletred ' +
    'midnightblue mintcream mistyrose moccasin navajowhite navy oldlace ' +
    'olive olivedrab orange orangered orchid palegoldenrod palegreen ' +
    'paleturquoise palevioletred papayawhip peachpuff peru pink plum ' +
    'powderblue purple rebeccapurple red rosybrown royalblue saddlebrown ' +
    'salmon sandybrown seagreen seashell sienna silver skyblue slateblue ' +
    'slategray slategrey snow springgreen steelblue tan teal thistle tomato ' +
    'turquoise violet wheat white whitesmoke yellow yellowgreen ' +
    'transparent currentcolor'
  ).split(' '),
)

const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const COLOR_FUNCTION = /^(?:rgba?|hsla?)\(([^()]*)\)$/i
/** One argument of rgb()/hsl(): a number with an optional unit, or `none`. */
const COLOR_ARG =
  /^(?:[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[-+]?\d+)?(?:%|deg|grad|rad|turn)?|none)$/i
/** Arguments are separated by spaces, commas and the alpha `/`. */
const colorArgsValid = (args: string) => {
  const parts = args.split(/[\s,/]+/).filter(Boolean)
  return (
    parts.length >= 3 &&
    parts.length <= 4 &&
    parts.every(p => COLOR_ARG.test(p))
  )
}
const CSS_VARIABLE = /^var\(\s*(--[a-z0-9_-]+)\s*(?:,([\s\S]*))?\)$/i

const trimmed = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const v = value.trim()
  return v && v.length <= MAX_LENGTH ? v : null
}

/**
 * The value when it is a CSS colour (hex, rgb()/rgba()/hsl()/hsla() with
 * numeric arguments, a colour name, or `var(--name)` with an optional
 * fallback that is itself a colour), trimmed; otherwise null.
 */
export function cssColor(value: unknown): string | null {
  const v = trimmed(value)
  if (!v) return null
  if (HEX_COLOR.test(v) || NAMED_COLORS.has(v.toLowerCase())) return v
  const fn = COLOR_FUNCTION.exec(v)
  if (fn) return colorArgsValid(fn[1]) ? v : null
  const variable = CSS_VARIABLE.exec(v)
  if (variable) {
    if (variable[2] === undefined) return v
    return cssColor(variable[2]) ? v : null
  }
  return null
}

/** A URL scheme at the start, e.g. `https:` or `javascript:`. */
const SCHEME = /^([a-z][a-z0-9+.-]*):/i

/**
 * Is this an https URL or a relative one (`/path`, `path`, `./path`,
 * `?q`, `#x`)? Protocol-relative URLs (`//host`, which also covers `/\host`
 * and `\\host`, both read as `//` by browsers) name another host, so they
 * are refused, as are every other scheme and control characters.
 */
export function isSafeCssUrl(value: unknown): value is string {
  const v = trimmed(value)
  if (!v) return false
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(v)) return false
  const scheme = SCHEME.exec(v)
  if (scheme) return scheme[1].toLowerCase() === 'https' && v.length > 8
  return !/^[/\\]{2}|^\\/.test(v)
}

/**
 * Escapes a value for a double-quoted CSS string: every character outside
 * a plain set (ASCII letters and digits, URL punctuation that has no meaning
 * in CSS strings, and non-ASCII text) becomes a hex escape, so quotes,
 * backslashes, parentheses, `<` (no `</style>`) and line breaks cannot end
 * the string or the element.
 */
const escapeCssString = (value: string) =>
  value.replace(
    /[^A-Za-z0-9 _.,:/?#&=%+~@!$*;[\]\u00a0-\uffff-]/g,
    ch => `\\${ch.charCodeAt(0).toString(16)} `,
  )

/** The value as a double-quoted, escaped CSS string, e.g. a font family name. */
export function cssString(value: string): string {
  // eslint-disable-next-line no-control-regex
  return `"${escapeCssString(value.replace(/[\u0000-\u001f\u007f]/g, ''))}"`
}

/**
 * `url("...")` for an https or relative URL (see isSafeCssUrl), escaped;
 * null for any other value, which then must not be written at all.
 */
export function cssUrl(value: unknown): string | null {
  if (!isSafeCssUrl(value)) return null
  return `url(${cssString(value.trim())})`
}

const FONT_WEIGHT = /^(?:normal|bold|bolder|lighter|[1-9]\d{0,2}|1000)$/i
const FONT_STYLE = /^(?:normal|italic|oblique)$/i

/** A CSS font-weight keyword or number (1 to 1000); otherwise null. */
export function cssFontWeight(value: unknown): string | null {
  const v = trimmed(value)
  return v && FONT_WEIGHT.test(v) ? v : null
}

/** `normal`, `italic` or `oblique`; otherwise null. */
export function cssFontStyle(value: unknown): string | null {
  const v = trimmed(value)
  return v && FONT_STYLE.test(v) ? v : null
}

/**
 * A class name or id fragment for a selector: only letters, digits, `-` and
 * `_` are kept.
 */
export const cssIdent = (value: unknown): string =>
  String(value ?? '').replace(/[^a-zA-Z0-9_-]/g, '')
